import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import { LearningFacadeService } from '../../../services/learning-facade.service';
import {
  SkillDocumentResponse,
  SkillSpecializationResponse,
  SkillProgressResponse,
  SkillTestResultResponse,
  SkillLearningGeneralSettingsResponse,
  PracticeLevelResponse,
  mergeSkillLearningGeneralSettings,
} from '../../../models/skill-learning.model';

export type SessionViewMode = 'level1' | 'level2' | 'level3';

@Component({
  selector: 'app-learning-session-container',
  templateUrl: './learning-session-container.component.html',
  styleUrls: ['./learning-session-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LearningSessionContainerComponent implements OnInit, OnDestroy {

  specialization: SkillSpecializationResponse;
  progress: SkillProgressResponse;
  generalSettings: SkillLearningGeneralSettingsResponse = mergeSkillLearningGeneralSettings(null);
  isLoading: boolean = true;
  hasError: boolean = false;
  viewMode: SessionViewMode = 'level1';

  private destroy$ = new Subject<void>();
  private specializationId: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private facade: LearningFacadeService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.specializationId = this.route.snapshot.params['specializationId'];
    if (!this.specializationId) {
      this.router.navigate(['/dashboard/learning']);
      return;
    }
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get currentLevel(): number {
    return this.facade.resolveCurrentLevel(this.progress);
  }

  get isLevel1Passed(): boolean {
    return this.facade.isLevel1Passed(this.progress);
  }

  get isLevel1SelfTestPassed(): boolean {
    return this.facade.isLevel1SelfTestPassed(this.progress);
  }

  get isLevel2Passed(): boolean {
    return this.facade.isLevel2Passed(this.progress);
  }

  get isLevel2Locked(): boolean {
    return !this.isLevel1Passed;
  }

  get isLevel3Locked(): boolean {
    return !this.isLevel2Passed;
  }

  get isLevel3InDevelopment(): boolean {
    return !this.hasPracticeLevelData(this.specialization && this.specialization.practiceLevel3);
  }

  get canCreateLevel1Task(): boolean {
    return !!this.progress &&
      this.isLevel1SelfTestPassed &&
      !this.isLevel1Passed &&
      !!this.progress.canCreateLevel1Task;
  }

  get shouldShowLevel1Task(): boolean {
    return !this.isLevel1Passed;
  }

  get shouldShowLevel1NextStepGuidance(): boolean {
    return !this.isLevel1Passed && !this.isLevel1SelfTestPassed;
  }

  get shouldEmphasizeLevel1TaskPlacement(): boolean {
    return this.shouldShowLevel1Task && this.isLevel1SelfTestPassed && !this.isLevel1Passed;
  }

  get hasActiveLevel1Task(): boolean {
    if (!this.progress) {
      return false;
    }
    return this.progress.level1TaskStatus === 'CREATED' || this.progress.level1TaskStatus === 'IN_WORK';
  }

  get isLevel1TaskStartDisabled(): boolean {
    return !this.canCreateLevel1Task && !this.hasActiveLevel1Task;
  }

  get isLevel2TaskStartDisabled(): boolean {
    return this.isLevel2Locked;
  }

  get isLevel3TaskStartDisabled(): boolean {
    return this.isLevel3Locked || this.isLevel3InDevelopment;
  }

  get documentUrlLevel1(): string | null {
    if (this.hasTheoryDocument) {
      return this.facade.buildDocumentDownloadUrl(this.specializationId, 1);
    }
    return null;
  }

  get theoryDocument(): SkillDocumentResponse | null {
    if (!this.specialization || !this.specialization.theoryLevel) {
      return null;
    }
    return this.specialization.theoryLevel.document;
  }

  get hasTheoryDocument(): boolean {
    if (!this.specialization || !this.specialization.theoryLevel) {
      return false;
    }
    return !!this.specialization.theoryLevel.document || !!this.specialization.theoryLevel.documentFileId;
  }

  get theoryDescription(): string {
    if (this.specialization && this.specialization.theoryLevel && this.specialization.theoryLevel.description) {
      return this.specialization.theoryLevel.description;
    }
    return this.specialization ? this.specialization.description || '' : '';
  }

  get level1TaskLockedMessageKey(): string {
    if (this.progress && this.progress.level1TaskStatus === 'CANCELLED' && !this.canCreateLevel1Task) {
      return 'SKILL_LEARNING_SESSION_LEVEL1_TASK_COOLDOWN';
    }
    return 'SKILL_LEARNING_SESSION_LEVEL1_TASK_LOCKED';
  }

  onSelectLevel(viewMode: SessionViewMode): void {
    this.viewMode = viewMode;
    this.cdr.markForCheck();
  }

  onTestCompleted(result: SkillTestResultResponse): void {
    if (result && result.passed) {
      this.loadProgress();
    }
    this.cdr.markForCheck();
  }

  onTestLevel2Requested(): void {
    this.onStartLevel2();
  }

  onStartLevel2(): void {
    this.viewMode = 'level2';
    this.cdr.markForCheck();
  }

  onStartLevel3(): void {
    this.viewMode = 'level3';
    this.cdr.markForCheck();
  }

  onPracticeLevelCompleted(): void {
    this.loadProgress();
    this.cdr.markForCheck();
  }

  onBackToList(): void {
    this.router.navigate(['/dashboard/learning']);
  }

  onTheoryDocumentDownload(): void {
    const url = this.documentUrlLevel1;
    if (url) {
      window.open(url, '_blank');
    }
  }

  private loadData(): void {
    this.isLoading = true;
    this.hasError = false;
    this.cdr.markForCheck();
    this.loadGeneralSettings();

    this.facade.fetchSpecializationDetails(this.specializationId)
      .takeUntil(this.destroy$)
      .subscribe(
        spec => {
          this.specialization = spec;
          this.loadProgress();
          this.cdr.markForCheck();
        },
        () => {
          this.hasError = true;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      );
  }

  private loadProgress(): void {
    this.facade.fetchUserProgress(this.specializationId)
      .takeUntil(this.destroy$)
      .subscribe(
        progress => {
          this.progress = progress;
          this.determineViewMode();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        () => {
          this.progress = this.buildEmptyProgress();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      );
  }

  private loadGeneralSettings(): void {
    this.facade.fetchGeneralSettings()
      .takeUntil(this.destroy$)
      .subscribe(
        settings => this.applyGeneralSettings(settings),
        () => this.applyGeneralSettings(null),
      );
  }

  private applyGeneralSettings(settings: SkillLearningGeneralSettingsResponse | null): void {
    this.generalSettings = mergeSkillLearningGeneralSettings(settings);
    this.cdr.markForCheck();
  }

  private determineViewMode(): void {
    if (this.viewMode === 'level2' || this.viewMode === 'level3') {
      return;
    }
    const level = this.currentLevel;
    if (level >= 2) {
      this.viewMode = 'level3';
    } else if (level >= 1) {
      this.viewMode = 'level2';
    } else {
      this.viewMode = 'level1';
    }
  }

  private buildEmptyProgress(): SkillProgressResponse {
    return {
      id: '',
      userId: '',
      specializationId: this.specializationId,
      currentLevel: 0,
      topicChecklist: {},
      testPassedAt: null,
      level1SelfTestPassedAt: null,
      level1SelfTestPassed: false,
      level1Task: null,
      canCreateLevel1Task: false,
      level1TaskCooldownUntil: null,
      level1TaskStatus: null,
      level2TaskStatus: null,
      level3TaskStatus: null,
    };
  }

  private hasPracticeLevelData(level: PracticeLevelResponse | null): boolean {
    if (!level) {
      return false;
    }
    return this.hasText(level.description) && (!!level.document || !!level.documentFileId);
  }

  private hasText(value: string): boolean {
    return !!value && value.trim().length > 0;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/user/learning-session/learning-session-container.component.ts